import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { setFlashToast } from '../../../utils/flashToast';
import * as nominationService from '../../../services/nominationCaseRequests';
import { getProfile, type ExtranetUserProfile } from '../../../services/userProfile';
import { focusFirstInvalid, validateRequired, type RequiredErrors, type RequiredFieldSpec } from '../../../utils/requiredValidation';
import { useNominationWizardContext } from '../../../hooks/useCaseRequestWizard';
import type { CriminalWizardDraft } from '../../../types/criminalCaseRequest';
import { useNominationLookups } from './useNominationLookups';
import { formatDisplayDate } from '../../../utils/dateFormat';
import { CriminalReviewDocumentsTable } from '../criminal/CriminalReviewDocumentsTable';
import { apiOrLookupLabel, deliveryMethodDisplay } from '../../view-request/viewRequestHelpers';
import {
  ReviewChips,
  ReviewField,
  ReviewGrid,
  ReviewSection,
  ReviewSubBlock,
} from '../criminal/CriminalReviewUi';
import CommonCoverNoteModal from '../../../components/wizard/CommonCoverNoteModal';
import { NominationWizardActions } from './NominationWizardActions';
import { nominationWizardPath } from './NominationWizardShell';
import { WizardReviewBreadcrumb, WizardReviewConfirm } from '../WizardReviewChrome';
import {
  wizardField,
  wizardReviewStepLabel,
  wizardSection,
  wizardStepTitle,
  wizardYesNo,
} from '../wizardI18nHelpers';
import { useRole } from '../../../rbac/hooks';
import { tasksListPathForRole } from '../../../rbac/tasksListPath';

export default function NominationReviewPage() {
  const nav = useNavigate();
  const { t } = useTranslation();
  const { roleCode } = useRole();
  const tasksListPath = tasksListPathForRole(roleCode);
  const { draft, setDraft } = useNominationWizardContext();
  const [errors, setErrors] = useState<RequiredErrors>({});
  const [profile, setProfile] = useState<ExtranetUserProfile | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [showCoverNote, setShowCoverNote] = useState(false);
  const [coverNoteRequestId, setCoverNoteRequestId] = useState('');
  const [coverNoteFileRef, setCoverNoteFileRef] = useState('');
  const lookups = useNominationLookups('review');

  useEffect(() => {
    let mounted = true;
    void getProfile().then((r) => {
      if (mounted && r.success) setProfile(r.data);
    });
    return () => {
      mounted = false;
    };
  }, []);

  const deliveryLabel = useMemo(
    () => deliveryMethodDisplay(draft as unknown as CriminalWizardDraft, lookups.deliveryMethods),
    [draft, lookups.deliveryMethods],
  );

  const connectedLabel = wizardYesNo(t, draft.hasConnectedMatters);

  const submit = async () => {
    if (submitting) return;

    const specs: RequiredFieldSpec[] = [
      { key: 'confirmCorrect', label: 'Confirmation', value: draft.confirmCorrect ? 'yes' : '', elementId: 'nom-confirm' },
    ];
    const reqErrs = validateRequired(specs);
    setErrors(reqErrs);
    if (Object.keys(reqErrs).length) {
      focusFirstInvalid(specs, reqErrs);
      return;
    }

    setSubmitting(true);
    const r = await nominationService.saveNominationRequest(draft, {
      isDraft: false,
      allSteps: true,
      confirmedCorrect: true,
    });
    if (!r.success) {
      setSubmitting(false);
      setFlashToast({ message: r.message ?? t('wizard.nav.submitFailed'), variant: 'error' });
      return;
    }
    setDraft((d) => ({
      ...d,
      draftRequestId: r.data.requestId,
      referenceNo: r.data.fileReferenceNumber ?? d.referenceNo,
    }));
    setFlashToast({ message: t('wizard.nav.submitSuccess'), variant: 'success' });
    setCoverNoteRequestId(r.data.requestId);
    setCoverNoteFileRef(r.data.fileReferenceNumber ?? draft.referenceNo ?? '');
    setShowCoverNote(true);
    setSubmitting(false);
  };

  const closeCoverNote = () => {
    setShowCoverNote(false);
    setCoverNoteRequestId('');
    setCoverNoteFileRef('');
    nav(tasksListPath);
  };

  const reviewCrumbTo = nominationWizardPath(1, draft.draftRequestId);
  const editTo = (step: 1 | 2 | 3 | 4) => nominationWizardPath(step, draft.draftRequestId);

  return (
    <div className="ccr-review-page">
      <CommonCoverNoteModal
        isOpen={showCoverNote}
        caseRequestId={coverNoteRequestId}
        fileRefFallback={coverNoteFileRef}
        showSuccessBanner
        onClose={closeCoverNote}
      />
      <WizardReviewBreadcrumb reviewCrumbTo={reviewCrumbTo} />

      <ReviewSection step={wizardReviewStepLabel(t, 1)} title={wizardStepTitle(t, 'nomination', 1)} editTo={editTo(1)}>
        <ReviewSubBlock title={wizardSection(t, 'caseCategorization')}>
          <ReviewGrid cols={4}>
            <ReviewField label={wizardField(t, 'fileCategory')} value={draft.fileCategory} />
            <ReviewField label={wizardField(t, 'subFileCategory')} value={draft.subFileCategoryName} />
          </ReviewGrid>
          <ReviewChips items={draft.applicableAreas} />
        </ReviewSubBlock>
        <ReviewSubBlock title={wizardSection(t, 'caseIntakeDetails')}>
          <ReviewField label={wizardField(t, 'description')} value={draft.description} fullWidth />
          <ReviewGrid cols={4}>
            <ReviewField label={wizardField(t, 'caseNumber')} value={draft.caseNumber} />
            <ReviewField label={wizardField(t, 'connectedMatters')} value={connectedLabel} />
            {draft.hasConnectedMatters === 'yes' ? (
              <>
                <ReviewField label={wizardField(t, 'connectedCaseNumber')} value={draft.connectedCaseNumber} />
                <ReviewField label={wizardField(t, 'connectedAgdFileRef')} value={draft.connectedAgdFileRef} />
              </>
            ) : null}
          </ReviewGrid>
        </ReviewSubBlock>
      </ReviewSection>

      <ReviewSection step={wizardReviewStepLabel(t, 2)} title={wizardStepTitle(t, 'nomination', 2)} editTo={editTo(2)}>
        <ReviewSubBlock title={wizardSection(t, 'timeline')}>
          <ReviewGrid cols={3}>
            <ReviewField
              label={wizardField(t, 'urgencyLevel')}
              value={apiOrLookupLabel(draft.urgencyLevelName, draft.urgencyLevelId, lookups.urgencyLevels)}
            />
            <ReviewField label={wizardField(t, 'deadlineIfAny')} value={formatDisplayDate(draft.deadline)} />
            <ReviewField label={wizardField(t, 'urgencyJustification')} value={draft.urgencyJustification} />
          </ReviewGrid>
        </ReviewSubBlock>
      </ReviewSection>

      <ReviewSection step={wizardReviewStepLabel(t, 3)} title={wizardStepTitle(t, 'nomination', 3)} editTo={editTo(3)}>
        <ReviewSubBlock title={wizardSection(t, 'requestSubmitterDetails')}>
          <ReviewGrid cols={4}>
            <ReviewField label={wizardField(t, 'submittedDivisionUnit')} value={profile?.divisionUnit?.trim() || profile?.externalAgencyName?.trim() || '—'} />
            <ReviewField label={wizardField(t, 'designation')} value={profile?.contactDesignation?.trim() || '—'} />
            <ReviewField label={wizardField(t, 'submittedBy')} value={profile?.displayName?.trim() || '—'} />
            <span className="ccr-review-grid-spacer" aria-hidden="true" />
            <ReviewField label={wizardField(t, 'emailAddress')} value={profile?.email?.trim() || '—'} />
            <ReviewField label={wizardField(t, 'contactNumber')} value={profile?.contactNumber?.trim() || '—'} />
          </ReviewGrid>
        </ReviewSubBlock>
        <ReviewSubBlock title={wizardSection(t, 'physicalFileDeliveryMethod')}>
          <ReviewField label={wizardField(t, 'deliveryMethod')} value={deliveryLabel} fullWidth />
        </ReviewSubBlock>
      </ReviewSection>

      <ReviewSection step={wizardReviewStepLabel(t, 4)} title={wizardStepTitle(t, 'nomination', 4)} editTo={editTo(4)}>
        <ReviewSubBlock>
          <CriminalReviewDocumentsTable documents={draft.documents} />
        </ReviewSubBlock>
      </ReviewSection>

      <WizardReviewConfirm
        id="nom-confirm"
        checked={draft.confirmCorrect}
        disabled={submitting}
        error={errors.confirmCorrect}
        onChange={(checked) => {
          setDraft((d) => ({ ...d, confirmCorrect: checked }));
          if (checked) setErrors((er) => ({ ...er, confirmCorrect: '' }));
        }}
      />

      <NominationWizardActions
        draft={draft}
        setDraft={setDraft}
        currentStep={4}
        onBack={() => nav(editTo(4))}
        showSubmit
        submitting={submitting}
        onSubmit={() => void submit()}
        reviewMode
      />
    </div>
  );
}
